import { getCentroidCoordinates } from "@phc/common"
import type { BiomeshSensorResponse } from "@phc/common/gen/threatAnalyticsGen"
import {
  OperationalStatus,
  SensorType,
  useListPlatformsBiomeshPlatformListGet,
  useListSensorsBiomeshSensorListGet,
} from "@phc/common/gen/threatAnalyticsGen"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useMap } from "react-map-gl/mapbox"
import {
  matchesSensorFilters,
  useBioMeshUIState,
} from "./BioMeshUIStateContext"

export interface StatusCounts {
  /** Full mission capability sensor count */
  fmc: number
  /** Partial mission capability sensor count */
  pmc: number
  /** No mission capability sensor count */
  nmc: number
  /** Unspecified mission capability sensor count */
  unspecified: number
}

export interface SensorTypeSummary extends StatusCounts {
  total: number
}

export interface SensorSummary {
  total: number
  status: StatusCounts
  byType: Map<SensorType, SensorTypeSummary>
}

const EMPTY_SUMMARY: SensorSummary = {
  total: 0,
  status: { fmc: 0, pmc: 0, nmc: 0, unspecified: 0 },
  byType: new Map(),
}

function countStatuses(sensors: BiomeshSensorResponse[]): StatusCounts {
  let fmc = 0
  let pmc = 0
  let nmc = 0
  let unspecified = 0
  for (const s of sensors) {
    switch (s.operational_status) {
      case OperationalStatus.OPERATIONAL_STATUS_FMC:
        fmc++
        break
      case OperationalStatus.OPERATIONAL_STATUS_PMC:
        pmc++
        break
      case OperationalStatus.OPERATIONAL_STATUS_NMC:
        nmc++
        break
      case OperationalStatus.OPERATIONAL_STATUS_UNSPECIFIED:
      default:
        unspecified++
    }
  }
  return { fmc, pmc, nmc, unspecified }
}

/**
 * Computes a summary of sensors currently visible in the map viewport,
 * grouped by sensor type with operational-status breakdowns.
 * Updates on map pan/zoom and filter changes.
 */
export function useSensorSummary(): SensorSummary {
  const { current: mapRef } = useMap()
  const { data: nodes } = useListPlatformsBiomeshPlatformListGet()
  const { data: sensors } = useListSensorsBiomeshSensorListGet({
    limit: 10_000,
  })
  const { filters } = useBioMeshUIState()
  const bound = useRef(false)

  // Build a lookup of platform_id → [lng, lat] for bounds checking
  const nodeCoords = useMemo(() => {
    const map = new Map<string, { lng: number; lat: number }>()
    if (!nodes) return map
    for (const p of nodes) {
      const coords = getCentroidCoordinates(p.centroid)
      if (coords) map.set(p.platform_id, coords)
    }
    return map
  }, [nodes])

  // Track which node IDs are in view
  const [visibleNodeIds, setVisibleNodeIds] = useState<Set<string>>(new Set())

  const updateVisible = useCallback(() => {
    const map = mapRef?.getMap()
    if (!map) return
    const { width, height } = map.getCanvas()
    // unproject returns unwrapped lng (can exceed ±180), so no antimeridian issues
    const nw = map.unproject([0, 0])
    const se = map.unproject([width, height])

    const ids = new Set<string>()
    const centerLng = (nw.lng + se.lng) / 2
    for (const [id, { lng, lat }] of nodeCoords) {
      if (lat < se.lat || lat > nw.lat) continue
      const adj = lng - Math.round((lng - centerLng) / 360) * 360
      if (adj >= nw.lng && adj <= se.lng) ids.add(id)
    }
    setVisibleNodeIds(ids)
  }, [mapRef, nodeCoords])

  useEffect(() => {
    const map = mapRef?.getMap()
    if (!map || bound.current) return
    bound.current = true
    map.on("moveend", updateVisible)
    const frame = requestAnimationFrame(updateVisible)
    return () => {
      cancelAnimationFrame(frame)
      map.off("moveend", updateVisible)
      bound.current = false
    }
  }, [mapRef, updateVisible])

  return useMemo(() => {
    if (!sensors) return EMPTY_SUMMARY

    // Filter sensors: must belong to a visible node and pass active filters
    const visible = sensors.filter(
      s =>
        s.platform_id != null &&
        visibleNodeIds.has(s.platform_id) &&
        matchesSensorFilters(filters, s)
    )

    if (visible.length === 0) return EMPTY_SUMMARY

    const status = countStatuses(visible)

    // Group by sensor type
    const groups = new Map<string, BiomeshSensorResponse[]>()
    for (const s of visible) {
      const arr = groups.get(s.sensor_type)
      if (arr) arr.push(s)
      else groups.set(s.sensor_type, [s])
    }

    const byType = new Map<SensorType, SensorTypeSummary>()
    for (const st of Object.values(SensorType)) {
      const group = groups.get(st)
      if (!group) continue
      byType.set(st, { total: group.length, ...countStatuses(group) })
    }

    return { total: visible.length, status, byType }
  }, [sensors, visibleNodeIds, filters])
}
